import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';

const TokenManager = ({ 
  contract, 
  governanceContract, 
  setError, 
  setSuccess, 
  setLoading, 
  loading, 
  onRedirectToGovernance,
  isOwner = false,
  isOperator = false,
  hasBoardSeats = false,
  boardSeats = 0
}) => {
  const [tokens, setTokens] = useState([]);
  const [tokenBalances, setTokenBalances] = useState({});
  const [ethBalance, setEthBalance] = useState('0');
  const [unallocatedEthBalance, setUnallocatedEthBalance] = useState('0');
  
  // New token state
  const [newToken, setNewToken] = useState({
    address: '',
    allocation: '',
    priceFeed: ''
  });

  // Governance and proposal states
  const [showProposalModal, setShowProposalModal] = useState(false);
  const [proposalAction, setProposalAction] = useState({
    type: '',
    description: '',
    params: {}
  });
  
  const [confirmationData, setConfirmationData] = useState({
    title: '',
    message: '',
    actionType: '',
    amount: '',
    onConfirm: () => {}
  });
  
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Allocation update modal state
  const [showAllocationModal, setShowAllocationModal] = useState(false);
  const [allocationModalData, setAllocationModalData] = useState({
    tokenAddress: '',
    currentAllocation: 0,
    newAllocation: 0,
    symbol: ''
  });

  // Edit mode states
  const [editMode, setEditMode] = useState(false);
  const [editedAllocations, setEditedAllocations] = useState({});
  const [originalAllocations, setOriginalAllocations] = useState({});
  const [showSellAllConfirm, setShowSellAllConfirm] = useState(false);
  const [tokenToSell, setTokenToSell] = useState(null);
  const [showSellConfirm, setShowSellConfirm] = useState(false);
  
  // Buy/Sell specific amount states
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [showSellAmountModal, setShowSellAmountModal] = useState(false);
  const [selectedToken, setSelectedToken] = useState(null);
  const [ethAmount, setEthAmount] = useState('');
  const [tokenAmount, setTokenAmount] = useState('');
  const [sellMax, setSellMax] = useState(false);
  
  // Total allocation tracking
  const [totalAllocation, setTotalAllocation] = useState(0);
  const [newTotalAllocation, setNewTotalAllocation] = useState(0);
  
  // Manual refresh counter
  const [refreshCounter, setRefreshCounter] = useState(0);
  
  // Transaction tracking
  const [lastTxHash, setLastTxHash] = useState('');
  const [txStatus, setTxStatus] = useState('');
  
  // Progress tracking for batch operations
  const [updateProgress, setUpdateProgress] = useState({ current: 0, total: 0, success: 0 });

  // Slippage settings
  const [globalSlippage, setGlobalSlippage] = useState(300); // Default 3%
  const [userSlippage, setUserSlippage] = useState(0); // 0 means use global
  const [slippageModalOpen, setSlippageModalOpen] = useState(false);
  const [currentSlippage, setCurrentSlippage] = useState(300);

  // Load tokens when component mounts, contract changes, or manual refresh
  useEffect(() => {
    if (contract) {
      loadTokensWithRetry();
      loadEthBalances();
      loadSlippageSettings();
    }
  }, [contract, refreshCounter]);

  // Calculate total allocation when tokens change
  useEffect(() => {
    if (tokens.length > 0) {
      const total = tokens.reduce((sum, token) => sum + parseInt(token.allocation), 0);
      setTotalAllocation(total);
      
      // Initialize edited allocations with current values
      if (!editMode) {
        const allocations = {};
        const originals = {};
        tokens.forEach(token => {
          allocations[token.address] = token.allocation;
          originals[token.address] = token.allocation;
        });
        setEditedAllocations(allocations);
        setOriginalAllocations(originals);
        setNewTotalAllocation(total);
      }
      
      // Load token balances
      loadTokenBalances();
    } else {
      setTotalAllocation(0);
      setNewTotalAllocation(0);
    }
  }, [tokens, editMode]);

  // Load slippage settings
  const loadSlippageSettings = async () => {
    try {
      if (contract && contract.signer) {
        const address = await contract.signer.getAddress();
        
        // Load global slippage
        const globalSlippageValue = await contract.slippageTolerance();
        setGlobalSlippage(globalSlippageValue.toNumber());
        
        // Load user-specific slippage from getSlippageTolerance function
        // First try to get the user-specific value through getSlippageTolerance
        const effectiveSlippage = await contract.getSlippageTolerance(address);
        const effectiveValue = effectiveSlippage.toNumber();
        setCurrentSlippage(effectiveValue);
        
        // If the effective value is different from global, the user has a custom setting
        if (effectiveValue !== globalSlippageValue.toNumber()) {
          setUserSlippage(effectiveValue);
        } else {
          setUserSlippage(0); // 0 means use global
        }
        
        console.log("Loaded slippage settings:", {
          global: globalSlippageValue.toNumber(),
          userEffective: effectiveValue,
          isUsingGlobal: effectiveValue === globalSlippageValue.toNumber()
        });
      }
    } catch (error) {
      console.error("Error loading slippage settings:", error);
    }
  };

  // Create our own version of the missing getSellTokenCallData function
  const createSellTokenCallData = (tokenAddress, amount) => {
    // We need to encode the function selector with parameters
    // For sellToken(address,uint256) the function signature would be sellToken(address,uint256)
    // and we need to encode that with the provided parameters
    
    try {
      // Create an ethers Interface with the function definition
      const iface = new ethers.utils.Interface([
        "function sellToken(address tokenAddress, uint256 amount)"
      ]);
      
      // Encode the function call with parameters
      const callData = iface.encodeFunctionData("sellToken", [tokenAddress, amount]);
      console.log(`Generated call data for sellToken: ${callData}`);
      return callData;
    } catch (error) {
      console.error("Error creating sell token call data:", error);
      throw error;
    }
  };
  
  // Create our own version of the missing getSellAllTokensCallData function
  const createSellAllTokensCallData = () => {
    try {
      // Create an ethers Interface with the function definition
      const iface = new ethers.utils.Interface([
        "function sellAllTokens()"
      ]);
      
      // Encode the function call with no parameters
      const callData = iface.encodeFunctionData("sellAllTokens", []);
      console.log(`Generated call data for sellAllTokens: ${callData}`);
      return callData;
    } catch (error) {
      console.error("Error creating sell all tokens call data:", error);
      throw error;
    }
  };
  
  // Create our own version of the missing getBuyTokenCallData function
  const createBuyTokenCallData = (tokenAddress, ethAmount) => {
    try {
      // Create an ethers Interface with the function definition
      const iface = new ethers.utils.Interface([
        "function buyToken(address tokenAddress, uint256 ethAmount)"
      ]);
      
      // Encode the function call with parameters
      const callData = iface.encodeFunctionData("buyToken", [tokenAddress, ethAmount]);
      console.log(`Generated call data for buyToken: ${callData}`);
      return callData;
    } catch (error) {
      console.error("Error creating buy token call data:", error);
      throw error;
    }
  };

  // ==========================================
  // GOVERNANCE-FIRST APPROACH IMPLEMENTATION
  // ==========================================
  
  // Check if user has permission (only the owner for direct contract actions)
  const hasPermission = () => {
    const hasOwnerPermission = !!isOwner;
    const hasOperatorPermission = !!isOperator;
    
    // Debug log
    console.log("Permission check:", {
      isOwner: hasOwnerPermission,
      isOperator: hasOperatorPermission,
      hasBoardSeats: !!hasBoardSeats,
      boardSeats: Number(boardSeats),
      hasPermission: hasOwnerPermission || hasOperatorPermission
    });
    
    // Only the contract owner or operator can directly execute transactions
    return hasOwnerPermission || hasOperatorPermission;
  };
  
  // Common handler for all actions that might need governance approval
  const handleGovernanceAction = (actionType, description, params, directAction) => {
    // Always check permission first
    if (!hasPermission()) {
      console.log(`No permission for action: ${description}. Creating governance proposal.`);
      handleUnauthorizedAction(actionType, description, params);
      return;
    }
    
    // If we have permission, execute the direct action
    console.log(`Has permission for action: ${description}. Executing directly.`);
    directAction();
  };
  
  // Function to handle unauthorized actions by redirecting to governance
  const handleUnauthorizedAction = (actionType, description, params = {}) => {
    console.log("Creating governance proposal for:", description, params);
    
    setProposalAction({
      type: actionType,
      description,
      params
    });
    setShowProposalModal(true);
  };

  // ==========================================
  // OPERATIONS AND ACTIONS
  // ==========================================

// Update global slippage tolerance
const updateGlobalSlippage = async () => {
  if (!contract) return;
  
  // Check if user has permission to execute directly
  if (hasPermission()) {
    // Direct execution for users with permission (same as current code)
    setLoading(true);
    setError('');
    setSuccess('');
    setTxStatus('Pending');
    
    try {
      console.log(`Updating global slippage to ${globalSlippage} basis points`);
      
      const tx = await contract.setSlippageTolerance(globalSlippage);
      setLastTxHash(tx.hash);
      
      console.log(`Transaction sent: ${tx.hash}`);
      setTxStatus('Mining');
      
      await tx.wait();
      setTxStatus('Confirmed');
      
      setSuccess(`Global slippage updated to ${globalSlippage/100}%`);
      setSlippageModalOpen(false);
      
      // Refresh settings
      loadSlippageSettings();
      
    } catch (error) {
      console.error("Error updating global slippage:", error);
      setError(error.message || "Failed to update global slippage");
      setTxStatus('Failed');
    } finally {
      setLoading(false);
    }
  } else {
    // Create governance proposal for users without permission
    setLoading(true);
    setError('');
    setSuccess('');
    setTxStatus('Pending');
    
    try {
      if (!governanceContract) {
        throw new Error("Governance contract not available");
      }
      
      // Create an ethers Interface with the function definition
      const iface = new ethers.utils.Interface([
        "function setSlippageTolerance(uint256 _slippageTolerance)"
      ]);
      
      // Encode the function call with parameters
      const callData = iface.encodeFunctionData("setSlippageTolerance", [globalSlippage]);
      
      // Create a governance proposal
      const description = `Update global slippage tolerance to ${globalSlippage/100}%`;
      const tx = await governanceContract.createProposal(
        6, // ProposalType.ChangeSlippageTolerance
        description,
        callData
      );
      
      setLastTxHash(tx.hash);
      console.log(`Proposal creation transaction sent: ${tx.hash}`);
      
      setTxStatus('Mining');
      await tx.wait();
      setTxStatus('Confirmed');
      
      setSuccess("Global slippage update proposal created successfully. It will need to be voted on and executed.");
      setSlippageModalOpen(false);
      
    } catch (error) {
      console.error("Error creating slippage update proposal:", error);
      setError(error.message || "Failed to create proposal");
      setTxStatus('Failed');
    } finally {
      setLoading(false);
    }
  }
};

  // Update user-specific slippage tolerance (no governance needed)
  const updateUserSlippage = async () => {
    if (!contract) return;
    
    setLoading(true);
    setError('');
    setSuccess('');
    setTxStatus('Pending');
    
    try {
      console.log(`Updating user slippage to ${userSlippage} basis points`);
      
      const tx = await contract.setUserSlippage(userSlippage);
      setLastTxHash(tx.hash);
      
      console.log(`Transaction sent: ${tx.hash}`);
      setTxStatus('Mining');
      
      await tx.wait();
      setTxStatus('Confirmed');
      
      setSuccess(`Your slippage tolerance updated to ${userSlippage/100}%`);
      setSlippageModalOpen(false);
      
      // Refresh settings
      loadSlippageSettings();
      
    } catch (error) {
      console.error("Error updating user slippage:", error);
      setError(error.message || "Failed to update user slippage");
      setTxStatus('Failed');
    } finally {
      setLoading(false);
    }
  };

  // Reset user slippage to use global (no governance needed)
  const resetUserSlippage = async () => {
    if (!contract) return;
    
    setLoading(true);
    setError('');
    setSuccess('');
    setTxStatus('Pending');
    
    try {
      console.log("Resetting user slippage to use global");
      
      // Set to 0 to use global
      const tx = await contract.setUserSlippage(0);
      setLastTxHash(tx.hash);
      
      console.log(`Transaction sent: ${tx.hash}`);
      setTxStatus('Mining');
      
      await tx.wait();
      setTxStatus('Confirmed');
      
      setSuccess(`Your slippage settings reset to use global (${globalSlippage/100}%)`);
      setUserSlippage(0);
      setSlippageModalOpen(false);
      
      // Refresh settings
      loadSlippageSettings();
      
    } catch (error) {
      console.error("Error resetting user slippage:", error);
      setError(error.message || "Failed to reset user slippage");
      setTxStatus('Failed');
    } finally {
      setLoading(false);
    }
  };

  // Load ETH balances
  const loadEthBalances = async () => {
    try {
      // Get contract's ETH balance
      const balance = await contract.provider.getBalance(contract.address);
      setEthBalance(ethers.utils.formatEther(balance));
      
      // Get unallocated ETH balance
      const unallocated = await contract.unallocatedEthBalance();
      setUnallocatedEthBalance(ethers.utils.formatEther(unallocated));
    } catch (error) {
      console.error("Error loading ETH balances:", error);
    }
  };
  
  // Load token balances
  const loadTokenBalances = async () => {
    try {
      const balances = {};
      
      for (const token of tokens) {
        try {
          // Create an ERC20 contract instance for the token
          const tokenContract = new ethers.Contract(
            token.address,
            [
              "function balanceOf(address) view returns (uint256)",
              "function decimals() view returns (uint8)",
              "function symbol() view returns (string)"
            ],
            contract.provider
          );
          
          // Get decimals
          const decimals = await tokenContract.decimals();
          
          // Get symbol (try-catch in case it fails)
          let symbol = "";
          try {
            symbol = await tokenContract.symbol();
          } catch {
            symbol = "???";
          }
          
          // Get balance
          const balance = await tokenContract.balanceOf(contract.address);
          
          balances[token.address] = {
            raw: balance,
            formatted: ethers.utils.formatUnits(balance, decimals),
            decimals,
            symbol
          };
        } catch (error) {
          console.error(`Error loading balance for token ${token.address}:`, error);
          balances[token.address] = {
            raw: ethers.BigNumber.from(0),
            formatted: "0",
            decimals: 18,
            symbol: "???"
          };
        }
      }
      
      setTokenBalances(balances);
    } catch (error) {
      console.error("Error loading token balances:", error);
    }
  };

  // Handle selling a specific token (all of it)
  const handleSellToken = (tokenAddress) => {
    // If owner, show confirmation dialog
    if (hasPermission()) {
      // Show confirmation dialog for owners
      setTokenToSell(tokenAddress);
      setShowSellConfirm(true);
    } else {
      // For users without permission, show sell amount modal to create a proposal
      const token = tokens.find(t => t.address === tokenAddress);
      if (token) {
        setSelectedToken(token);
        setTokenAmount('');
        setSellMax(false);
        setShowSellAmountModal(true);
      }
    }
  };
  
  // Handle selling a specific amount of a token
  const handleSellSpecificAmount = (token) => {
    // If owner, show sell amount modal
    if (hasPermission()) {
      // Show sell amount modal for owners
      setSelectedToken(token);
      setTokenAmount('');
      setSellMax(false);
      setShowSellAmountModal(true);
    } else {
      // For users without permission, show sell amount modal to create a proposal
      setSelectedToken(token);
      setTokenAmount('');
      setSellMax(false);
      setShowSellAmountModal(true);
    }
  };
  
  // Handle buying a specific token
  const handleBuyToken = (token) => {
    if (hasPermission()) {
      // Show buy modal for users with permission
      setSelectedToken(token);
      setEthAmount('');
      setShowBuyModal(true);
    } else {
      // For users without permission, show a message about creating a proposal
      setConfirmationData({
        title: 'Create Buy Token Proposal',
        message: `Would you like to create a proposal to buy token ${token.address.substring(0, 8)}...?`,
        actionType: 'buy token proposal',
        amount: '',
        onConfirm: () => {
          // Show buy modal for setting amount
          setSelectedToken(token);
          setEthAmount('');
          setShowBuyModal(true);
        }
      });
      setShowConfirmation(true);
    }
  };

  // Handler to hide confirmation dialog
  const hideSellConfirmation = () => {
    setShowSellConfirm(false);
    setTokenToSell(null);
  };
  
  // Handler to hide sell amount modal
  const hideSellAmountModal = () => {
    setShowSellAmountModal(false);
    setSelectedToken(null);
    setTokenAmount('');
    setSellMax(false);
  };
  
  // Handler to hide buy modal
  const hideBuyModal = () => {
    setShowBuyModal(false);
    setSelectedToken(null);
    setEthAmount('');
  };

  // Handler to hide slippage modal
  const hideSlippageModal = () => {
    setSlippageModalOpen(false);
  };

  // Execute token sell transaction (sell all)
  const executeSellToken = async () => {
    if (!tokenToSell) return;
    
    // Check if user has permission to execute directly
    if (hasPermission()) {
      setLoading(true);
      setError('');
      setSuccess('');
      setTxStatus('Pending');
      setShowSellConfirm(false);
      
      try {
        console.log(`Selling token: ${tokenToSell}`);
        
        const tx = await contract.sellToken(tokenToSell, 0); // 0 means sell all
        setLastTxHash(tx.hash);
        
        console.log(`Transaction sent: ${tx.hash}`);
        setTxStatus('Mining');
        
        await tx.wait();
        setTxStatus('Confirmed');
        
        setSuccess(`Token ${tokenToSell.substring(0, 8)}... successfully sold`);
        
        // Wait a moment before refreshing
        await new Promise(resolve => setTimeout(resolve, 1000));
        forceRefresh();
        
      } catch (error) {
        console.error("Error selling token:", error);
        setError(error.message || "Failed to sell token");
        setTxStatus('Failed');
      } finally {
        setLoading(false);
        setTokenToSell(null);
      }
    } else {
      // Create governance proposal for selling tokens
      setLoading(true);
      setError('');
      setSuccess('');
      setTxStatus('Pending');
      setShowSellConfirm(false);
      
      try {
        if (!governanceContract) {
          throw new Error("Governance contract not available");
        }
        
        // Get encoded call data for selling token using our custom function
        const callData = createSellTokenCallData(tokenToSell, 0); // 0 means sell all
        
        // Create a governance proposal
        const description = `Sell all of token ${tokenToSell.substring(0, 8)}...`;
        const tx = await governanceContract.createProposal(
          4, // ProposalType.SellToken or appropriate type
          description,
          callData
        );
        
        setLastTxHash(tx.hash);
        console.log(`Proposal creation transaction sent: ${tx.hash}`);
        
        setTxStatus('Mining');
        await tx.wait();
        setTxStatus('Confirmed');
        
        setSuccess(`Token sell proposal created successfully. It will need to be voted on and executed.`);
        
      } catch (error) {
        console.error("Error creating token sell proposal:", error);
        setError(error.message || "Failed to create proposal");
        setTxStatus('Failed');
      } finally {
        setLoading(false);
        setTokenToSell(null);
      }
    }
  };
  
  // Execute selling specific amount of tokens
  const executeSellSpecificAmount = async () => {
    if (!selectedToken) return;
    
    let amount;
    
    if (sellMax) {
      // Sell all tokens
      amount = 0; // 0 means sell all in our contract implementation
    } else {
      // Make sure we have a valid amount
      if (!tokenAmount || parseFloat(tokenAmount) <= 0) {
        setError("Please enter a valid amount to sell");
        return;
      }
      
      // Convert to token units based on decimals
      const decimals = tokenBalances[selectedToken.address]?.decimals || 18;
      amount = ethers.utils.parseUnits(tokenAmount, decimals);
      
      // Make sure it's not more than balance
      const balance = tokenBalances[selectedToken.address]?.raw || ethers.BigNumber.from(0);
      if (amount.gt(balance)) {
        setError("Amount exceeds token balance");
        return;
      }
    }
    
    // Check if user has permission to execute directly
    if (hasPermission()) {
      setLoading(true);
      setError('');
      setSuccess('');
      setTxStatus('Pending');
      setShowSellAmountModal(false);
      
      try {
        console.log(`Selling ${sellMax ? 'all' : tokenAmount} of token: ${selectedToken.address}`);
        
        const tx = await contract.sellToken(selectedToken.address, sellMax ? 0 : amount);
        setLastTxHash(tx.hash);
        
        console.log(`Transaction sent: ${tx.hash}`);
        setTxStatus('Mining');
        
        await tx.wait();
        setTxStatus('Confirmed');
        
        setSuccess(`Successfully sold ${sellMax ? 'all' : tokenAmount} of token ${selectedToken.address.substring(0, 8)}...`);
        
        // Wait a moment before refreshing
        await new Promise(resolve => setTimeout(resolve, 1000));
        forceRefresh();
        
      } catch (error) {
        console.error("Error selling token amount:", error);
        setError(error.message || "Failed to sell token");
        setTxStatus('Failed');
      } finally {
        setLoading(false);
        setSelectedToken(null);
        setTokenAmount('');
        setSellMax(false);
      }
    } else {
      // Create governance proposal for selling tokens
      setLoading(true);
      setError('');
      setSuccess('');
      setTxStatus('Pending');
      setShowSellAmountModal(false);
      
      try {
        if (!governanceContract) {
          throw new Error("Governance contract not available");
        }
        
        // Get encoded call data for selling token using our custom function
        const callData = createSellTokenCallData(
          selectedToken.address,
          sellMax ? 0 : amount
        );
        
        // Create a governance proposal
        const sellAmountDescription = sellMax ? "all" : tokenAmount;
        const description = `Sell ${sellAmountDescription} of token ${selectedToken.address.substring(0, 8)}...`;
        const tx = await governanceContract.createProposal(
          4, // ProposalType.SellToken or appropriate type
          description,
          callData
        );
        
        setLastTxHash(tx.hash);
        console.log(`Proposal creation transaction sent: ${tx.hash}`);
        
        setTxStatus('Mining');
        await tx.wait();
        setTxStatus('Confirmed');
        
        setSuccess(`Token sell proposal created successfully. It will need to be voted on and executed.`);
        
      } catch (error) {
        console.error("Error creating token sell proposal:", error);
        setError(error.message || "Failed to create proposal");
        setTxStatus('Failed');
      } finally {
        setLoading(false);
        setSelectedToken(null);
        setTokenAmount('');
        setSellMax(false);
      }
    }
  };
  
  // Execute buying a token with ETH
  const executeBuyToken = async () => {
    if (!selectedToken) return;
    
    // Make sure we have a valid amount
    if (!ethAmount || parseFloat(ethAmount) <= 0) {
      setError("Please enter a valid ETH amount");
      return;
    }
    
    // Convert to wei
    const ethAmountWei = ethers.utils.parseEther(ethAmount);
    
    // Check if we have enough unallocated ETH
    if (ethAmountWei.gt(ethers.utils.parseEther(unallocatedEthBalance))) {
      setError("Amount exceeds unallocated ETH balance");
      return;
    }
    
    if (hasPermission()) {
      // Direct execution for owners
      setLoading(true);
      setError('');
      setSuccess('');
      setTxStatus('Pending');
      setShowBuyModal(false);
      
      try {
        console.log(`Buying token: ${selectedToken.address} with ${ethAmount} ETH`);
        
        const tx = await contract.buyToken(selectedToken.address, ethAmountWei);
        setLastTxHash(tx.hash);
        
        console.log(`Transaction sent: ${tx.hash}`);
        setTxStatus('Mining');
        
        await tx.wait();
        setTxStatus('Confirmed');
        
        setSuccess(`Successfully bought token ${selectedToken.address.substring(0, 8)}... with ${ethAmount} ETH`);
        
        // Wait a moment before refreshing
        await new Promise(resolve => setTimeout(resolve, 1000));
        forceRefresh();
        
      } catch (error) {
        console.error("Error buying token:", error);
        setError(error.message || "Failed to buy token");
        setTxStatus('Failed');
      } finally {
        setLoading(false);
        setSelectedToken(null);
        setEthAmount('');
      }
    } else {
      // Create governance proposal
      setLoading(true);
      setError('');
      setSuccess('');
      setTxStatus('Pending');
      setShowBuyModal(false);
      
      try {
        if (!governanceContract) {
          throw new Error("Governance contract not available");
        }
        
        // Get encoded call data for buying token
        const callData = createBuyTokenCallData(
          selectedToken.address,
          ethAmountWei
        );
        
        // Create a governance proposal
        const description = `Buy token ${selectedToken.address.substring(0, 8)}... with ${ethAmount} ETH`;
        const tx = await governanceContract.createProposal(
          3, // ProposalType.BuyToken or appropriate type
          description,
          callData
        );
        
        setLastTxHash(tx.hash);
        console.log(`Proposal creation transaction sent: ${tx.hash}`);
        
        setTxStatus('Mining');
        await tx.wait();
        setTxStatus('Confirmed');
        
        setSuccess(`Token buy proposal created successfully. It will need to be voted on and executed.`);
        
      } catch (error) {
        console.error("Error creating buy token proposal:", error);
        setError(error.message || "Failed to create proposal");
        setTxStatus('Failed');
      } finally {
        setLoading(false);
        setSelectedToken(null);
        setEthAmount('');
      }
    }
  };

  // Handle selling all tokens
  const handleSellAllTokens = () => {
    // Check if user has permission to execute directly
    if (hasPermission()) {
      // Show confirmation dialog for users with permission
      setShowSellAllConfirm(true);
    } else {
      // Create governance proposal for selling all tokens
      setConfirmationData({
        title: 'Create Proposal to Sell All Tokens',
        message: 'Are you sure you want to create a governance proposal to sell ALL tokens in the fund?',
        actionType: 'sell all tokens proposal',
        amount: '',
        onConfirm: createSellAllTokensProposal
      });
      setShowConfirmation(true);
    }
  };

  // Create proposal to sell all tokens
  const createSellAllTokensProposal = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    setTxStatus('Pending');
    
    try {
      if (!governanceContract) {
        throw new Error("Governance contract not available");
      }
      
      // Get encoded call data for selling all tokens
      const callData = createSellAllTokensCallData();
      
      // Create a governance proposal
      const description = `Sell all tokens in the fund`;
      const tx = await governanceContract.createProposal(
        5, // ProposalType.SellAllTokens or appropriate type
        description,
        callData
      );
      
      setLastTxHash(tx.hash);
      console.log(`Proposal creation transaction sent: ${tx.hash}`);
      
      setTxStatus('Mining');
      await tx.wait();
      setTxStatus('Confirmed');
      
      setSuccess(`Sell all tokens proposal created successfully. It will need to be voted on and executed.`);
      
    } catch (error) {
      console.error("Error creating sell all tokens proposal:", error);
      setError(error.message || "Failed to create proposal");
      setTxStatus('Failed');
    } finally {
      setLoading(false);
    }
  };

  // Execute selling all tokens
  const executeSellAllTokens = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    setTxStatus('Pending');
    setShowSellAllConfirm(false);
    
    try {
      console.log("Selling all tokens");
      
      const tx = await contract.sellAllTokens();
      setLastTxHash(tx.hash);
      
      console.log(`Transaction sent: ${tx.hash}`);
      setTxStatus('Mining');
      
      await tx.wait();
      setTxStatus('Confirmed');
      
      setSuccess("All tokens successfully sold");
      
      // Wait a moment before refreshing
      await new Promise(resolve => setTimeout(resolve, 1000));
      forceRefresh();
      
    } catch (error) {
      console.error("Error selling all tokens:", error);
      setError(error.message || "Failed to sell all tokens");
      setTxStatus('Failed');
    } finally {
      setLoading(false);
    }
  };

  // Handler to show confirmation dialog
  const showSellAllConfirmation = () => {
    setShowSellAllConfirm(true);
  };

  // Handler to hide confirmation dialog
  const hideSellAllConfirmation = () => {
    setShowSellAllConfirm(false);
  };

  // Load tokens with retry logic to ensure data is fresh
  const loadTokensWithRetry = async (attemptCount = 0) => {
    try {
      setLoading(true);
      console.log(`Loading tokens (attempt ${attemptCount + 1})`);
      
      // Get token count first to ensure we know how many tokens to load
      const tokenCount = await contract.getTokenCount();
      console.log(`Token count from contract: ${tokenCount}`);
      
      // Get total allocation to verify our calculations
      const totalAlloc = await contract.getTotalAllocation();
      console.log(`Total allocation from contract: ${totalAlloc.toString()}`);
      
      const tokensArray = [];
      
      // Load each token's info
      for (let index = 0; index < tokenCount; index++) {
        try {
          const tokenInfo = await contract.getTokenInfo(index);
          console.log(`Token ${index}:`, {
            address: tokenInfo.tokenAddress,
            allocation: tokenInfo.allocation.toString(),
            priceFeed: tokenInfo.priceFeed
          });
          
          tokensArray.push({
            index,
            address: tokenInfo.tokenAddress,
            allocation: tokenInfo.allocation.toString(),
            priceFeed: tokenInfo.priceFeed
          });
        } catch (error) {
          console.error(`Error loading token at index ${index}:`, error);
        }
      }
      
      if (tokensArray.length !== Number(tokenCount)) {
        console.warn(`Expected ${tokenCount} tokens but loaded ${tokensArray.length}`);
      }
      
      console.log("Final loaded tokens:", tokensArray);
      setTokens(tokensArray);
      
      // Calculate our own total for verification
      const calculatedTotal = tokensArray.reduce((sum, token) => sum + parseInt(token.allocation), 0);
      console.log(`Calculated total allocation: ${calculatedTotal}, Contract total: ${totalAlloc.toString()}`);
      
      // If the calculated total doesn't match the contract total and we haven't retried too many times,
      // retry after a short delay
      if (calculatedTotal.toString() !== totalAlloc.toString() && attemptCount < 3) {
        console.warn("Allocation mismatch, will retry loading...");
        setTimeout(() => loadTokensWithRetry(attemptCount + 1), 2000);
        return;
      }
      
    } catch (error) {
      console.error("Error loading tokens:", error);
      setError(`Error loading tokens: ${error.message || "Unknown error"}`);
    } finally {
      setLoading(false);
    }
  };

  // Dedicated function to force refresh
  const forceRefresh = () => {
    setRefreshCounter(prev => prev + 1);
  };

  // Handle adding a new token
  const handleAddToken = async () => {
    if (!newToken.address || !newToken.allocation || !newToken.priceFeed) {
      setError("Please fill all token fields");
      return;
    }

    // Validate Ethereum addresses
    if (!ethers.utils.isAddress(newToken.address)) {
      setError("Invalid token address format");
      return;
    }

    if (!ethers.utils.isAddress(newToken.priceFeed)) {
      setError("Invalid price feed address format");
      return;
    }

    // Check if total allocation will exceed 100%
    const newAllocation = parseInt(newToken.allocation);
    const newTotal = totalAllocation + newAllocation;
    if (newTotal > 10000) {
      setError(`Total allocation cannot exceed 100%. Current: ${(totalAllocation / 100).toFixed(2)}%, Adding: ${(newAllocation / 100).toFixed(2)}%`);
      return;
    }

    handleGovernanceAction(
      3, // AddToken type
      `Add token ${newToken.address.substring(0, 8)}... with ${(newAllocation / 100).toFixed(2)}% allocation`,
      { 
        tokenAddress: newToken.address,
        tokenAllocation: newAllocation,
        tokenPriceFeed: newToken.priceFeed
      },
      async () => {
        setLoading(true);
        setError('');
        setSuccess('');
        setTxStatus('Pending');

        try {
          // Use governanceContract directly from component props (already destructured in function params)
          if (!governanceContract) {
            throw new Error("Governance contract not available");
          }
          
          // Get encoded call data for adding a token
          const callData = await governanceContract.getAddTokenCallData(
            newToken.address,
            newAllocation,
            newToken.priceFeed
          );
          
          // Create a governance proposal
          const description = `Add token ${newToken.address.substring(0, 8)}... with ${(newAllocation / 100).toFixed(2)}% allocation`;
          const tx = await governanceContract.createProposal(
            3, // ProposalType.AddToken
            description,
            callData
          );
          
          setLastTxHash(tx.hash);
          console.log(`Proposal creation transaction sent: ${tx.hash}`);
          
          setTxStatus('Mining');
          await tx.wait();
          setTxStatus('Confirmed');
          
          setSuccess("Token addition proposal created successfully. It will need to be voted on and executed.");
          setNewToken({ address: '', allocation: '', priceFeed: '' });
          
        } catch (error) {
          console.error("Error creating token addition proposal:", error);
          setError(error.message || "Failed to create proposal");
          setTxStatus('Failed');
        } finally {
          setLoading(false);
        }
      }
    );
  };

  // Function to show the allocation update modal
  const showAllocationUpdateModal = (tokenAddress, currentAllocation) => {
    const token = tokens.find(t => t.address === tokenAddress);
    const symbol = tokenBalances[tokenAddress]?.symbol || '';
    
    setAllocationModalData({
      tokenAddress,
      currentAllocation,
      newAllocation: currentAllocation,
      symbol
    });
    setShowAllocationModal(true);
  };
  
  // Function to handle creating the governance proposal with the user's chosen allocation
  const createAllocationUpdateProposal = async () => {
    const { tokenAddress, newAllocation } = allocationModalData;
    
    setLoading(true);
    setError('');
    setSuccess('');
    setTxStatus('Pending');
    setShowAllocationModal(false);
    
    try {
      if (!governanceContract) {
        throw new Error("Governance contract not available");
      }
      
      // Get encoded call data for token allocation update
      const callData = await governanceContract.getUpdateTokenAllocationCallData(
        tokenAddress,
        newAllocation
      );
      
      // Create a governance proposal
      const description = `Update allocation for token ${tokenAddress.substring(0, 8)}... to ${(newAllocation / 100).toFixed(2)}%`;
      const tx = await governanceContract.createProposal(
        5, // ProposalType.UpdateTokenAllocation
        description,
        callData
      );
      
      setLastTxHash(tx.hash);
      console.log(`Proposal creation transaction sent: ${tx.hash}`);
      
      setTxStatus('Mining');
      await tx.wait();
      setTxStatus('Confirmed');
      
      setSuccess("Token allocation update proposal created successfully. It will need to be voted on and executed.");
      
    } catch (error) {
      console.error("Error creating token allocation update proposal:", error);
      setError(error.message || "Failed to create proposal");
      setTxStatus('Failed');
    } finally {
      setLoading(false);
    }
  };

  // Handle edit mode for allocations
  const handleEnterEditMode = () => {
    setEditMode(true);
    
    // Copy current allocations to edited allocations
    const allocations = {};
    const originals = {};
    tokens.forEach(token => {
      allocations[token.address] = token.allocation;
      originals[token.address] = token.allocation;
    });
    setEditedAllocations(allocations);
    setOriginalAllocations(originals);
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setEditedAllocations({});
  };

  const copyToClipboard = (text) => {
    console.log(text);
    navigator.clipboard.writeText(text).then(() => {});
  };

  // Handle allocation changes while in edit mode
  const handleAllocationChange = (tokenAddress, newValue) => {
    const newAllocations = { ...editedAllocations };
    
    // Ensure input is a valid number
    const allocationValue = parseInt(newValue) || 0;
    newAllocations[tokenAddress] = allocationValue.toString();
    
    // Calculate new total allocation
    let newTotal = 0;
    Object.values(newAllocations).forEach(value => {
      newTotal += parseInt(value) || 0;
    });
    
    setEditedAllocations(newAllocations);
    setNewTotalAllocation(newTotal);
    
    console.log(`Token ${tokenAddress} allocation changed to ${allocationValue}, new total: ${newTotal}`);
  };

  // Function to update a single token allocation
  const handleUpdateSingleAllocation = (tokenAddress, newAllocation) => {
    // If user has permission, allow direct update
    if (hasPermission()) {
      handleGovernanceAction(
        5, // UpdateTokenAllocation type
        `Update allocation for token ${tokenAddress.substring(0, 8)}...`,
        { 
          tokenAddress: tokenAddress,
          tokenAllocation: newAllocation
        },
        async () => {
          setLoading(true);
          setError('');
          setSuccess('');
          setTxStatus('Pending');
          
          try {
            console.log(`Updating single token allocation: ${tokenAddress} to ${newAllocation}`);
            
            // Use the single token update function
            const tx = await contract.updateTokenAllocation(tokenAddress, newAllocation);
            setLastTxHash(tx.hash);
            
            console.log(`Transaction sent: ${tx.hash}`);
            setTxStatus('Mining');
            
            await tx.wait();
            setTxStatus('Confirmed');
            
            setSuccess(`Successfully updated allocation for ${tokenAddress.substring(0, 8)}...`);
            
            // Wait a moment before refreshing
            await new Promise(resolve => setTimeout(resolve, 1000));
            forceRefresh();
            
          } catch (error) {
            console.error("Error updating token allocation:", error);
            setError(`Failed to update allocation: ${error.message || "Unknown error"}`);
            setTxStatus('Failed');
          } finally {
            setLoading(false);
          }
        }
      );
    } else {
      // For users without permission, skip the governance action check and 
      // directly show the allocation modal to create a proposal
      const token = tokens.find(t => t.address === tokenAddress);
      const currentAllocation = token ? parseInt(token.allocation) : 0;
      showAllocationUpdateModal(tokenAddress, currentAllocation);
    }
  };

  // Dedicated function to update allocations using individual transaction for each token
  const handleSaveAllocations = async () => {
    if (newTotalAllocation > 10000) {
      setError("Total allocation cannot exceed 100%");
      return;
    }

    // Collect tokens with changed allocations
    const updates = [];
    for (const token of tokens) {
      const oldAllocation = parseInt(originalAllocations[token.address]);
      const newAllocation = parseInt(editedAllocations[token.address]);
      
      // Only include if allocation changed
      if (oldAllocation !== newAllocation) {
        updates.push({
          address: token.address,
          oldAllocation,
          newAllocation
        });
      }
    }
    
    console.log(`Found ${updates.length} tokens with changed allocations`);
    
    if (updates.length === 0) {
      setSuccess("No allocation changes detected");
      setEditMode(false);
      setLoading(false);
      return;
    }
    
    // Create params object with all allocation changes
    const params = {};
    updates.forEach(update => {
      params[update.address] = update.newAllocation;
    });
    
    handleGovernanceAction(
      0, // Using generic governance action type
      `Update allocations for multiple tokens`,
      params,
      async () => {
        // For users with permission, process the updates directly
        if (hasPermission()) {
          setLoading(true);
          setError('');
          setSuccess('');
          
          setUpdateProgress({ current: 0, total: updates.length, success: 0 });
          
          // Process one update at a time
          for (let i = 0; i < updates.length; i++) {
            const update = updates[i];
            setUpdateProgress(prev => ({ ...prev, current: i + 1 }));
            
            try {
              console.log(`Updating token ${i+1}/${updates.length}: ${update.address} from ${update.oldAllocation} to ${update.newAllocation}`);
              setTxStatus(`Processing ${i+1}/${updates.length}`);
              
              // Use individual update for each token
              const tx = await contract.updateTokenAllocation(update.address, update.newAllocation);
              setLastTxHash(tx.hash);
              
              console.log(`Transaction sent: ${tx.hash}`);
              setTxStatus(`Mining ${i+1}/${updates.length}`);
              
              await tx.wait();
              console.log(`Transaction confirmed for token ${update.address}`);
              
              setUpdateProgress(prev => ({ ...prev, success: prev.success + 1 }));
              
              // Small delay between transactions
              if (i < updates.length - 1) {
                await new Promise(resolve => setTimeout(resolve, 2000));
              }
              
            } catch (error) {
              console.error(`Error updating token ${update.address}:`, error);
              setError(`Failed to update token ${i+1}/${updates.length}: ${error.message || "Unknown error"}`);
              setTxStatus('Failed');
              
              // Continue with next token despite error
              continue;
            }
          }
          
          // Final status
          if (updateProgress.success === updates.length) {
            setSuccess(`Successfully updated all ${updates.length} token allocations`);
            setTxStatus('Confirmed');
          } else {
            setSuccess(`Updated ${updateProgress.success}/${updates.length} token allocations`);
            setTxStatus('Partial');
          }
          
          setEditMode(false);
          
          // Wait a moment before refreshing
          await new Promise(resolve => setTimeout(resolve, 2000));
          forceRefresh();
          
          setLoading(false);
        } else {
          // For users without permission, show a modal to create a proposal for batch updates
          // We could implement a batch allocation update proposal here
          setSuccess("Batch allocation updates through governance are not yet implemented. Please update allocations one by one.");
          setEditMode(false);
        }
      }
    );
  };

  // Implementation for token removal
  const handleRemoveToken = async (tokenAddress) => {
    handleGovernanceAction(
      4, // RemoveToken type
      `Remove token ${tokenAddress.substring(0, 8)}...`,
      { tokenAddress: tokenAddress },
      async () => {
        // For users with permission, execute directly
        if (hasPermission()) {
          setLoading(true);
          setError('');
          setSuccess('');
          setTxStatus('Pending');
          
          try {
            console.log(`Removing token: ${tokenAddress}`);
            
            const tx = await contract.removeToken(tokenAddress);
            setLastTxHash(tx.hash);
            
            console.log(`Transaction sent: ${tx.hash}`);
            setTxStatus('Mining');
            
            await tx.wait();
            setTxStatus('Confirmed');
            
            setSuccess(`Token ${tokenAddress.substring(0, 8)}... successfully removed`);
            setEditMode(false);
            
            // Wait a moment before refreshing
            await new Promise(resolve => setTimeout(resolve, 1000));
            forceRefresh();
            
          } catch (error) {
            console.error("Error removing token:", error);
            setError(error.message || "Failed to remove token");
            setTxStatus('Failed');
          } finally {
            setLoading(false);
          }
        } else {
          // For users without permission, create a governance proposal
          if (!governanceContract) {
            throw new Error("Governance contract not available");
          }
          
          setLoading(true);
          setError('');
          setSuccess('');
          setTxStatus('Pending');
          
          try {
            // Get encoded call data for removing token
            const callData = await governanceContract.getRemoveTokenCallData(tokenAddress);
            
            // Create a governance proposal
            const description = `Remove token ${tokenAddress.substring(0, 8)}...`;
            const tx = await governanceContract.createProposal(
              4, // ProposalType.RemoveToken
              description,
              callData
            );
            
            setLastTxHash(tx.hash);
            console.log(`Proposal creation transaction sent: ${tx.hash}`);
            
            setTxStatus('Mining');
            await tx.wait();
            setTxStatus('Confirmed');
            
            setSuccess("Token removal proposal created successfully. It will need to be voted on and executed.");
            setEditMode(false);
            
          } catch (error) {
            console.error("Error creating token removal proposal:", error);
            setError(error.message || "Failed to create proposal");
            setTxStatus('Failed');
          } finally {
            setLoading(false);
          }
        }
      }
    );
  };

  // Function to format an address for display
  const formatAddress = (address) => {
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
  };

  // Render modals
  const renderProposalModal = () => {
    if (!showProposalModal) return null;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-lg max-w-md w-full">
          <h3 className="text-xl font-bold mb-4 text-blue-600">Create Governance Proposal</h3>
          <p className="mb-4">
            You don't have direct permission to perform this action. Would you like to create a governance proposal instead?
          </p>
          <div className="mb-4 p-3 bg-gray-50 rounded">
            <p className="font-medium">{proposalAction.description}</p>
          </div>
          
          <div className="flex justify-end space-x-4">
            <button
              className="px-4 py-2 bg-gray-300 rounded-md hover:bg-gray-400"
              onClick={() => setShowProposalModal(false)}
            >
              Cancel
            </button>
            <button
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              onClick={() => {
                // Redirect to governance tab
                if (typeof onRedirectToGovernance === 'function') {
                  onRedirectToGovernance(proposalAction);
                } else {
                  setSuccess("To perform this action, you'll need to create a governance proposal. Please go to the Governance tab.");
                }
                setShowProposalModal(false);
              }}
            >
              Create Proposal
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render the confirmation modal for governance actions
  const renderConfirmationModal = () => {
    if (!showConfirmation) return null;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-lg max-w-md w-full">
          <h3 className="text-xl font-bold mb-4 text-blue-600">{confirmationData.title}</h3>
          <p className="mb-4">{confirmationData.message}</p>
          
          <div className="flex justify-end space-x-4">
            <button
              className="px-4 py-2 bg-gray-300 rounded-md hover:bg-gray-400"
              onClick={() => setShowConfirmation(false)}
            >
              Cancel
            </button>
            <button
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              onClick={() => {
                setShowConfirmation(false);
                confirmationData.onConfirm();
              }}
              disabled={loading}
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    );
  };
  
  // Render the allocation update modal
  const renderAllocationUpdateModal = () => {
    if (!showAllocationModal) return null;
    
    // Calculate the total allocation if we update this token
    const currentTokenAlloc = parseInt(allocationModalData.currentAllocation);
    const newTokenAlloc = parseInt(allocationModalData.newAllocation);
    const allocDifference = newTokenAlloc - currentTokenAlloc;
    const newTotal = totalAllocation + allocDifference;
    const isValidAllocation = newTokenAlloc > 0 && newTotal <= 10000;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white p-6 rounded-lg max-w-md w-full">
          <h3 className="text-xl font-bold mb-4 text-blue-600">Update Token Allocation</h3>
          
          <div className="mb-4">
            <p className="text-sm mb-1">Token:</p>
            <p className="font-mono text-sm break-all bg-gray-100 p-2 rounded">
              {formatAddress(allocationModalData.tokenAddress)}
              {allocationModalData.symbol ? ` (${allocationModalData.symbol})` : ''}
            </p>
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Current Allocation: {(allocationModalData.currentAllocation / 100).toFixed(2)}%
            </label>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              New Allocation (in basis points, 100 = 1%)
            </label>
            <input
              type="number"
              className="w-full p-2 border rounded-md"
              value={allocationModalData.newAllocation}
              onChange={(e) => setAllocationModalData({
                ...allocationModalData,
                newAllocation: parseInt(e.target.value) || 0
              })}
              placeholder="1000"
              min="0"
              max="10000"
            />
            <p className="text-sm text-gray-500 mt-1">
              {(allocationModalData.newAllocation / 100).toFixed(2)}%
            </p>
          </div>
          
          {/* Show new total allocation info */}
          <div className="mb-4 p-3 bg-gray-50 rounded">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">New Total Allocation:</span>
              <span className={`text-sm font-bold ${isValidAllocation ? 'text-green-600' : 'text-red-600'}`}>
                {(newTotal / 100).toFixed(2)}%
              </span>
            </div>
            {!isValidAllocation && newTotal > 10000 && (
              <p className="text-xs text-red-600 mt-1">
                Total allocation cannot exceed 100%
              </p>
            )}
          </div>
          
          <div className="flex justify-end space-x-4">
            <button
              className="px-4 py-2 bg-gray-300 rounded-md hover:bg-gray-400"
              onClick={() => setShowAllocationModal(false)}
            >
              Cancel
            </button>
            <button
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              onClick={createAllocationUpdateProposal}
              disabled={loading || !isValidAllocation}
            >
              Create Proposal
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg border p-6">
      <h2 className="text-xl font-semibold mb-4">Token Management</h2>
      
      {/* Render the modals */}
      {renderProposalModal()}
      {renderConfirmationModal()}
      {renderAllocationUpdateModal()}
      
      {/* Slippage Settings Modal */}
      {slippageModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full">
            <h3 className="text-xl font-bold mb-4 text-blue-600">Slippage Tolerance Settings</h3>
            
            <div className="mb-4">
              <h4 className="font-medium mb-2">Global Slippage (for all users)</h4>
              <div className="flex items-center space-x-2">
                <select 
                  className="p-2 border rounded"
                  value={globalSlippage}
                  onChange={(e) => setGlobalSlippage(parseInt(e.target.value))}
                  disabled={!contract || loading}
                >
                  <option value="50">0.5%</option>
                  <option value="100">1%</option>
                  <option value="200">2%</option>
                  <option value="300">3%</option>
                  <option value="500">5%</option>
                </select>
                <button 
                  className="bg-blue-500 text-white px-3 py-2 rounded"
                  onClick={updateGlobalSlippage}
                  disabled={!contract || loading}
                >
                  Update Global Slippage
                </button>
              </div>
            </div>
            
            <div className="mb-4 border-t pt-4">
              <h4 className="font-medium mb-2">Your Personal Slippage</h4>
              <div className="flex items-center space-x-2 mb-2">
                <select 
                  className="p-2 border rounded"
                  value={userSlippage}
                  onChange={(e) => setUserSlippage(parseInt(e.target.value))}
                  disabled={!contract || loading}
                >
                  <option value="0">Use Global Setting</option>
                  <option value="50">0.5%</option>
                  <option value="100">1%</option>
                  <option value="200">2%</option>
                  <option value="300">3%</option>
                  <option value="500">5%</option>
                </select>
                <button 
                  className="bg-green-500 text-white px-3 py-2 rounded"
                  onClick={updateUserSlippage}
                  disabled={!contract || loading || userSlippage === 0}
                >
                  Set My Slippage
                </button>
              </div>
              
              <button 
                className="text-red-500 hover:text-red-700 text-sm underline"
                onClick={resetUserSlippage}
                disabled={!contract || loading || userSlippage === 0}
              >
                Reset to use global setting
              </button>
            </div>
            
            <div className="mb-4 bg-blue-50 p-3 rounded">
              <p className="text-sm">
                <span className="font-medium">Current effective slippage: </span> 
                {currentSlippage/100}% 
                {userSlippage > 0 ? " (your personal setting)" : " (global setting)"}
              </p>
              <p className="text-xs mt-1 text-gray-600">
                This is the maximum price difference acceptable when trading tokens.
                Higher values may result in trades completing more reliably but potentially at worse prices.
              </p>
            </div>
            
            <div className="flex justify-end">
              <button
                className="px-4 py-2 bg-gray-300 rounded-md hover:bg-gray-400"
                onClick={hideSlippageModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Sell All Tokens Confirmation Modal */}
      {showSellAllConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full">
            <h3 className="text-xl font-bold mb-4 text-red-600">⚠️ CAUTION: Sell ALL Tokens</h3>
            <p className="mb-4">
              You are about to sell ALL tokens in the fund. This action will:
            </p>
            <ul className="list-disc ml-5 mb-4 text-sm">
              <li>Convert all token holdings to ETH</li>
              <li>This ETH will be added to the unallocated ETH balance</li>
              <li>Your allocations will remain but with zero token balances</li>
            </ul>
            <p className="mb-4 font-semibold">
              Are you sure you want to proceed with complete liquidation?
            </p>
            <div className="flex justify-end space-x-4">
              <button
                className="px-4 py-2 bg-gray-300 rounded-md hover:bg-gray-400"
                onClick={hideSellAllConfirmation}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                onClick={executeSellAllTokens}
              >
                Yes, Sell ALL Tokens
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Sell Token Confirmation Modal */}
      {showSellConfirm && tokenToSell && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full">
            <h3 className="text-xl font-bold mb-4 text-orange-600">⚠️ Sell Entire Token</h3>
            <p className="mb-4">
              You are about to sell all of this token's holdings:
            </p>
            <p className="mb-4 font-mono bg-gray-100 p-2 rounded text-sm break-all">
              {tokenToSell}
            </p>
            <p className="mb-4 font-semibold">
              Are you sure you want to proceed?
            </p>
            <div className="flex justify-end space-x-4">
              <button
                className="px-4 py-2 bg-gray-300 rounded-md hover:bg-gray-400"
                onClick={hideSellConfirmation}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700"
                onClick={executeSellToken}
              >
                Yes, Sell Token
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Sell Specific Amount Modal */}
      {showSellAmountModal && selectedToken && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full">
            <h3 className="text-xl font-bold mb-4 text-orange-600">Sell Token</h3>
            <p className="mb-2">
              <span className="font-medium">Token:</span> {formatAddress(selectedToken.address)}
              {tokenBalances[selectedToken.address]?.symbol ? ` (${tokenBalances[selectedToken.address].symbol})` : ''}
            </p>
            <p className="mb-4">
              <span className="font-medium">Available Balance:</span> {tokenBalances[selectedToken.address]?.formatted || '0'}
            </p>
            
            <div className="mb-4">
              <label className="flex items-center space-x-2 mb-2">
                <input
                  type="checkbox"
                  checked={sellMax}
                  onChange={(e) => setSellMax(e.target.checked)}
                  className="rounded"
                />
                <span>Sell entire balance</span>
              </label>
              
              {!sellMax && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount to Sell
                  </label>
                  <input
                    type="number"
                    className="w-full p-2 border rounded-md"
                    value={tokenAmount}
                    onChange={(e) => setTokenAmount(e.target.value)}
                    placeholder={`Enter amount (max: ${tokenBalances[selectedToken.address]?.formatted || '0'})`}
                    min="0"
                    step="0.000001"
                    disabled={sellMax}
                  />
                </div>
              )}
            </div>
            
            <div className="flex justify-end space-x-4">
              <button
                className="px-4 py-2 bg-gray-300 rounded-md hover:bg-gray-400"
                onClick={hideSellAmountModal}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700"
                onClick={executeSellSpecificAmount}
                disabled={!sellMax && (!tokenAmount || parseFloat(tokenAmount) <= 0)}
              >
                {hasPermission() ? "Sell Tokens" : "Create Sell Proposal"}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Buy Token Modal */}
      {showBuyModal && selectedToken && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full">
            <h3 className="text-xl font-bold mb-4 text-green-600">Buy Token</h3>
            <p className="mb-2">
              <span className="font-medium">Token:</span> {formatAddress(selectedToken.address)}
              {tokenBalances[selectedToken.address]?.symbol ? ` (${tokenBalances[selectedToken.address].symbol})` : ''}
            </p>
            <p className="mb-2">
              <span className="font-medium">Current Balance:</span> {tokenBalances[selectedToken.address]?.formatted || '0'}
            </p>
            <p className="mb-4">
              <span className="font-medium">Available ETH:</span> {unallocatedEthBalance} ETH
            </p>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ETH Amount to Use
              </label>
              <input
                type="number"
                className="w-full p-2 border rounded-md"
                value={ethAmount}
                onChange={(e) => setEthAmount(e.target.value)}
                placeholder={`Enter ETH amount (max: ${unallocatedEthBalance})`}
                min="0"
                step="0.01"
              />
            </div>
            
            <div className="flex justify-end space-x-4">
              <button
                className="px-4 py-2 bg-gray-300 rounded-md hover:bg-gray-400"
                onClick={hideBuyModal}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                onClick={executeBuyToken}
                disabled={!ethAmount || parseFloat(ethAmount) <= 0 || parseFloat(ethAmount) > parseFloat(unallocatedEthBalance)}
              >
                {hasPermission() ? "Buy Tokens" : "Create Buy Proposal"}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Transaction Status */}
      {lastTxHash && (
        <div className="mb-4 p-3 bg-blue-50 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="font-medium">Transaction Status:</span>
            <span className={`text-sm font-bold ${
              txStatus === 'Confirmed' ? 'text-green-600' : 
              txStatus === 'Failed' ? 'text-red-600' : 
              txStatus === 'Partial' ? 'text-yellow-600' : 'text-orange-600'
            }`}>
              {txStatus}
            </span>
          </div>
          {updateProgress.total > 0 && (
            <div className="mt-1 text-sm">
              Progress: {updateProgress.current}/{updateProgress.total} ({updateProgress.success} successful)
            </div>
          )}
          <div className="text-xs mt-1">
            <a 
              href={`https://goerli.etherscan.io/tx/${lastTxHash}`} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              View on Etherscan: {lastTxHash.substring(0, 16)}...{lastTxHash.substring(lastTxHash.length - 16)}
            </a>
          </div>
        </div>
      )}
      
      {/* Fund Balances */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
        <div className="p-3 bg-blue-50 rounded-lg">
          <div className="text-sm text-blue-600">Total ETH Balance</div>
          <div className="text-xl font-semibold">{ethBalance} ETH</div>
        </div>
        <div className="p-3 bg-green-50 rounded-lg">
          <div className="text-sm text-green-600">Unallocated ETH</div>
          <div className="text-xl font-semibold">{unallocatedEthBalance} ETH</div>
          <div className="text-xs text-gray-500">Available for buying tokens</div>
        </div>
        <div className="p-3 bg-purple-50 rounded-lg cursor-pointer" onClick={() => setSlippageModalOpen(true)}>
          <div className="text-sm text-purple-600">Slippage Tolerance</div>
          <div className="text-xl font-semibold">{currentSlippage/100}%</div>
          <div className="text-xs text-gray-500">Click to configure</div>
        </div>
        <div className="p-3 bg-orange-50 rounded-lg">
          <div className="text-sm text-orange-600">Sell All Tokens</div>
          <button
            className="mt-1 px-3 py-1 bg-orange-600 text-white text-sm rounded-md hover:bg-orange-700"
            onClick={handleSellAllTokens}
            disabled={loading || tokens.length === 0}
          >
            Liquidate All Positions
          </button>
        </div>
      </div>
      
      {/* Refresh control */}
      <div className="mb-4 p-3 bg-gray-50 rounded-lg">
        <div className="flex justify-between items-center">
          <span className="text-sm">Last refresh: {new Date().toLocaleTimeString()}</span>
          <button
            className="bg-green-600 text-white px-4 py-2 rounded-md text-sm hover:bg-green-700"
            onClick={forceRefresh}
            disabled={loading}
          >
            {loading ? "Loading..." : "Refresh Data"}
          </button>
        </div>
        <div className="mt-2 text-sm text-gray-600">
          <p className="font-medium">Governance-First Mode:</p>
          <p>All token management actions now go through governance proposals when you don't have direct permissions.</p>
        </div>
      </div>
      
      {/* Allocation display */}
      <div className="mb-4 p-3 bg-gray-50 rounded-lg">
        <div className="flex justify-between items-center">
          <span className="font-medium">Current Total Allocation:</span>
          <span className={`font-bold ${totalAllocation !== 10000 ? 'text-red-600' : 'text-blue-600'}`}>
            {(totalAllocation / 100).toFixed(2)}%
          </span>
        </div>
        
        {/* Progress bar for allocation */}
        <div className="mt-2 w-full bg-gray-200 rounded-full h-2.5">
          <div 
            className={`h-2.5 rounded-full ${totalAllocation !== 10000 ? 'bg-red-600' : 'bg-blue-600'}`} 
            style={{ width: `${Math.min(totalAllocation / 100, 100)}%` }}
          ></div>
        </div>
        
        {/* Show warning if allocation is not 100% */}
        {totalAllocation !== 10000 && (
          <p className="mt-2 text-sm text-yellow-600">
            Note: Total allocation is {(totalAllocation / 100).toFixed(2)}%. You can save under 100% but the Fund is underallocated.
          </p>
        )}
      </div>
      
      {/* Current Tokens Table */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-3">
          <h3 className="font-medium">Current Tokens ({tokens.length})</h3>
          
          {/* Edit mode toggle */}
          {tokens.length > 0 && !editMode && (
            <button
              className="bg-blue-600 text-white px-3 py-1 rounded-md text-sm"
              onClick={handleEnterEditMode}
              disabled={loading}
            >
              Edit Allocations
            </button>
          )}
          
          {editMode && (
            <div className="flex space-x-2">
              {/* Allocation display while editing */}
              <div className={`text-sm font-medium ${newTotalAllocation !== 10000 ? 'text-red-600' : 'text-green-600'}`}>
                New Total: {(newTotalAllocation / 100).toFixed(2)}%
              </div>
              
              <button
                className="bg-green-600 text-white px-3 py-1 rounded-md text-sm disabled:bg-gray-400"
                onClick={handleSaveAllocations}
                disabled={loading || newTotalAllocation > 10000}
              >
                Save Changes
              </button>
              
              <button
                className="bg-gray-500 text-white px-3 py-1 rounded-md text-sm"
                onClick={handleCancelEdit}
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Token Address</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Balance</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Allocation (%)</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Price Feed</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {tokens.map((token, index) => (
                <tr key={index} className={editMode ? 'bg-blue-50' : ''}>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                    {formatAddress(token.address)}
                    {tokenBalances[token.address]?.symbol ? ` (${tokenBalances[token.address].symbol})` : ''}
                    <button type="button" onClick={() => copyToClipboard(token.address)} className="ml-2" title="Copy address">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="#1f1f1f" viewBox="0 -960 960 960"><path d="M360-240q-33 0-56.5-23.5T280-320v-480q0-33 23.5-56.5T360-880h360q33 0 56.5 23.5T800-800v480q0 33-23.5 56.5T720-240H360Zm0-80h360v-480H360v480ZM200-80q-33 0-56.5-23.5T120-160v-560h80v560h440v80H200Zm160-240v-480 480Z"/></svg>
                    </button>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-500">
                    {tokenBalances[token.address]?.formatted || '0'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-500">
                    {editMode ? (
                      <input
                        type="number"
                        className={`w-24 p-1 border rounded ${
                          parseInt(editedAllocations[token.address] || '0') === 0 ? 'bg-red-100' : ''
                        }`}
                        value={editedAllocations[token.address] || '0'}
                        onChange={(e) => handleAllocationChange(token.address, e.target.value)}
                        min="0"
                        max="10000"
                      />
                    ) : (
                      `${(parseInt(token.allocation) / 100).toFixed(2)}%`
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-500">
                    {formatAddress(token.priceFeed)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-center">
                    {editMode ? (
                      <div className="flex justify-center space-x-2">
                        <button
                          className="text-red-600 hover:text-red-800 px-2 py-1"
                          onClick={() => handleRemoveToken(token.address)}
                          disabled={loading}
                        >
                          Remove
                        </button>
                        <button
                          className="text-orange-600 hover:text-orange-800 px-2 py-1"
                          onClick={() => handleSellToken(token.address)}
                          disabled={loading}
                        >
                          Sell All
                        </button>
                      </div>
                    ) : (
                      <div className="flex justify-center space-x-1">
                        <button
                          className="text-green-600 hover:text-green-800 border border-green-300 rounded px-2 py-1"
                          onClick={() => handleBuyToken(token)}
                          disabled={loading || parseFloat(unallocatedEthBalance) <= 0}
                          title="Buy tokens with ETH"
                        >
                          Buy
                        </button>
                        <button
                          className="text-orange-600 hover:text-orange-800 border border-orange-300 rounded px-2 py-1"
                          onClick={() => handleSellSpecificAmount(token)}
                          disabled={loading || parseFloat(tokenBalances[token.address]?.formatted || '0') <= 0}
                          title="Sell specific amount of tokens"
                        >
                          Sell
                        </button>
                        <button
                          className="text-blue-600 hover:text-blue-800 border border-blue-300 rounded px-2 py-1"
                          onClick={() => handleUpdateSingleAllocation(token.address, parseInt(token.allocation))}
                          disabled={loading}
                          title="Update this token's allocation"
                        >
                          Update %
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {tokens.length === 0 && (
          <div className="text-center py-4 text-gray-500">
            No tokens added yet.
          </div>
        )}
      </div>
      
      {/* Add New Token Form */}
      <div className="border rounded-lg p-4">
        <h3 className="font-medium mb-3">Add New Token</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Token Address
            </label>
            <input
              type="text"
              className="w-full p-2 border rounded-md"
              value={newToken.address}
              onChange={(e) => setNewToken({...newToken, address: e.target.value})}
              placeholder="0x..."
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Allocation (in basis points, 100 = 1%)
            </label>
            <input
              type="number"
              className="w-full p-2 border rounded-md"
              value={newToken.allocation}
              onChange={(e) => setNewToken({...newToken, allocation: e.target.value})}
              placeholder="1000"
              min="0"
              max="10000"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Price Feed Address
            </label>
            <input
              type="text"
              className="w-full p-2 border rounded-md"
              value={newToken.priceFeed}
              onChange={(e) => setNewToken({...newToken, priceFeed: e.target.value})}
              placeholder="0x..."
            />
          </div>
          
          <button
            className="w-full bg-blue-600 text-white py-2 rounded-md hover:bg-blue-700 disabled:bg-gray-400"
            onClick={handleAddToken}
            disabled={loading || !newToken.address || !newToken.allocation || !newToken.priceFeed}
          >
            {loading ? "Processing..." : "Add Token"}
          </button>
        </div>
      </div>
      
      {/* Information section */}
      <div className="mt-4 p-4 bg-yellow-50 border-l-4 border-yellow-400 text-yellow-700">
        <h4 className="font-bold">Important Notes about Token Management</h4>
        <ul className="list-disc ml-5 mt-1 text-sm">
          <li className="mb-1">Token allocations should always sum to exactly 100%.</li>
          <li className="mb-1">You can directly remove tokens using the 'Remove' button when in edit mode.</li>
          <li className="mb-1">After changing allocations, a portfolio rebalance is required to adjust actual holdings.</li>
          <li className="mb-1">Buy tokens using unallocated ETH - check your balance in the dashboard above.</li>
          <li className="mb-1">Sell specific amounts of tokens to fine-tune your positions.</li>
          <li className="mb-1">When you sell tokens, the ETH received is added to your unallocated ETH balance.</li>
          <li className="mb-1">Configure slippage tolerance to control price impact during trades.</li>
          <li>Use "Sell All Tokens" for emergency liquidation of the entire fund.</li>
        </ul>
      </div>
    </div>
  );
};

export default TokenManager;